import { Currency, ValidatorsProps } from '@ui/src';
import { DocumentComment } from '../common/documents/types';
import { OrganizationType, ApplicationScheme } from '../common/types';

export enum ChannelStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export interface Client {
  id: string;
  email?: string;
  phone: string;
  url?: string;
  status: 'ACTIVE' | 'DISABLED';
  profile?: {
    firstName: string;
    lastName: string;
  };
  totpEnabled?: boolean;
}

export interface Channel {
  id: string;
  status: ChannelStatus;
  employee?: {
    profile?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
}

export enum ApplicationDocumentType {
  APPLICATION_INCORPORATION_CERTIFICATE = 'APPLICATION_INCORPORATION_CERTIFICATE',
  APPLICATION_ARTICLES_OF_ASSOCIATIONS = 'APPLICATION_ARTICLES_OF_ASSOCIATIONS',
  APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS = 'APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS',
  APPLICATION_REGISTRY_OF_THE_DIRECTORS = 'APPLICATION_REGISTRY_OF_THE_DIRECTORS',
  APPLICATION_ANNUAL_ACCOUNTS = 'APPLICATION_ANNUAL_ACCOUNTS',
  OTHER = 'OTHER',
}

export enum AccessRight {
  NONE = 'NONE',
  VIEWER = 'VIEWER',
}

export enum NewApplicationStatus {
  INCOMPLETE = 'INCOMPLETE',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
}

export enum NewApplicationSumSubStatus {
  RED = 'RED',
  GREEN = 'GREEN',
}

export interface NewApplicationValues {
  applicationSchemeId?: string;
  userId?: string;
  organizationId?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  dob: string;
  monthlyTurnoverRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  nationalityId?: string;
  clientActivityId?: string;
  sourceOfFundsIds?: string[];
  productIds?: string[];
  deliveryChannelId?: string;
  isUSResident: boolean;
  participatesInPolitics: boolean;
  typeOfActivityDescription?: string;
  sourceOfFundsDescription?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  nameOfEmployer?: string;
  applicantId?: string;
}

export interface NewApplicationComment {
  id: string;
  date: string;
  author: {
    firstName: string;
    lastName: string;
  };
  text: string;
}

export enum NewApplicationStepStatus {
  COMPLETED = 'Completed',
  IN_PROGRESS = 'In progress',
  AWAITING = 'Awaiting',
}

export enum DocumentType {
  IDENTIFICATION_DOCUMENTS = 'identificationDocuments',
  TRANSACTION_DOCUMENTS = 'transactionDocuments',
  PSP_CUSTOMER_DOCUMENTS = 'pspCustomerDocuments',
  CDD_CHECK_DOCUMENTS = 'cddCheckDocuments',
  OTHER_DOCUMENTS = 'otherDocuments',
  UNSORTED_DOCUMENTS = 'unsorted',
}

export interface Match {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  userId?: string;
  selected?: boolean;
  isClient?: boolean;
  type: ApplicationType;
  companyName?: string;
}

export interface NewApplicationsValidatorResponse {
  createClientApplicationSchema: ValidatorsProps;
  updateClientApplicationSchema: ValidatorsProps;
  updateClientApplicationDataSchema: ValidatorsProps;
}

export enum ReviewStatus {
  NONE = 'NONE',
  HAS_DUPLICATE = 'HAS_DUPLICATE',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export interface NewApplicationLog {
  id: string;
  applicationId: string;
  updatedByUserId: string;
  property: string;
  value: string;
  createdAt: string;
  updatedByUser?: {
    email?: string;
    profile?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

export enum COFStatus {
  APPROVED = 'Approved',
  ACTIVE = 'Active',
  INCOMPLETE = 'Incomplete',
  SUSPENDED = 'Suspended',
  DISABLED = 'Disabled',
  DECLINED = 'Declined',
  WARNING = 'Warning',
}

export enum ApplicationType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
  MAIN_REPRESENTATIVE = 'MAIN_REPRESENTATIVE',
}

export enum SourceOfFundsEnum {
  WAGES = 'WAGES',
  INDIVIDUAL_ACTIVITIES = 'INDIVIDUAL_ACTIVITIES',
  SCHOLARSHIP = 'SCHOLARSHIP',
  INCOME_FROM_FAMILY_MEMBERS_OR_CLOSE_RELATIVES = 'INCOME_FROM_FAMILY_MEMBERS_OR_CLOSE_RELATIVES',
  SAVINGS = 'SAVINGS',
  INHERITANCE = 'INHERITANCE',
  SECURITIES = 'SECURITIES',
  REAL_ESTATE = 'REAL_ESTATE',
  LOANS_BORROWED_FUNDS = 'LOANS_BORROWED_FUNDS',
  WINNINGS = 'WINNINGS',
  OTHER = 'OTHER',
}

export enum DeliveryChannelEnum {
  IN_PERSON = 'IN_PERSON',
  REMOTE = 'REMOTE',
  VIA_THIRD_PARTY_AGENT = 'VIA_THIRD_PARTY_AGENT',
}

export enum ActivitiyOfTheClientEnum {
  EMPLOYMENT = 'EMPLOYMENT',
  RETIREE = 'RETIREE',
  STUDENT = 'STUDENT',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  UNEMPLOYED = 'UNEMPLOYED',
  OTHER = 'OTHER',
}

export enum SumSubReviewStatus {
  PENDING = 'PENDING',
  IN_PROCCESS = 'IN_PROCCESS',
  COMPLETED = 'COMPLETED',
}

export interface SumSubApplication {
  applicantId: string;
  buttonIds: string[];
  clientComment: string;
  createdAt: string;
  externalUserId: string;
  id: string;
  inspectionId: string;
  modComment: string;
  rejectLabels: string[];
  rejectType: string;
  reviewAnswer: NewApplicationSumSubStatus;
  reviewStatus: SumSubReviewStatus;
  userId: string;
}

export interface FormValues {
  id?: string;
  userId?: string;
  organizationId?: string;
  email?: string;
  dob?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  city?: string;
  postalCode?: string;
  address1?: string;
  address2?: string;
  countryId?: string;
  sumSubApplicationId?: string;
  status?: string;
  sumSubStatus?: NewApplicationSumSubStatus;
  reviewStatus?: ReviewStatus;
  reviewDuplicatesComment?: string;
  duplicates?: Match[];
  reviewedDuplicates?: string[];
  passportNumber?: string;
  passportExpiredDate?: string;
  participatesInPolitics?: string | boolean;
  isUSResident?: string | boolean;
  monthlyTurnoverRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  sourceOfFundsIds?: string[];
  clientActivityId?: string;
  deliveryChannelId?: string;
  productIds?: string[];
  nationalityId?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  typeOfActivityDescription?: string;
  sourceOfFundsDescription?: string;
  taxNumber?: string;
  placeOfBirth?: string;
  birthCountryId?: string;
  correspondenceAddressSameAsRegisteredAddress?: boolean;
  correspondenceCountryId?: string;
  correspondenceCity?: string;
  correspondencePostalCode?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
}

export interface NewApplicationIndividualStepsStatuses {
  stepOne: NewApplicationStepStatus;
  stepTwo: NewApplicationStepStatus;
  stepThree: NewApplicationStepStatus;
  stepFour?: NewApplicationStepStatus;
  stepFive?: NewApplicationStepStatus;
}

export interface ApplicationComment {
  applicationId: string;
  text: string;
  userId: string;
  user: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  id: string;
  createdAt: string;
  updatedAt: string;
}

export interface SumSubDocument {
  applicationId: string;
  createdAt: string;
  deletedAt: string;
  expDate: string;
  id: string;
  number: string;
  type: string;
  updatedAt: string;
}

export interface NewApplicationListItem {
  applicationSchemeId?: string;
  applicationScheme?: ApplicationScheme;
  externalUserId: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  city: string;
  postalCode: string;
  address1: string;
  address2: string;
  companyName?: string;
  dob: string;
  status: NewApplicationStatus;
  taxNumber?: string;
  url?: string;
  idDocs: {
    edges: {
      node: SumSubDocument;
    }[];
  };
  countryId: string;
  userId: string;
  organizationId: string;
  reviewStatus: ReviewStatus;
  reviewDuplicatesComment?: string;
  sumSubApplicationId: string;
  sumSubApplication: SumSubApplication;
  companyOfficialType: ApplicationType;
  type: ApplicationType;
  country: {
    id: string;
    name: string;
  };
  reviewEmployeeId?: string;
  reviewEmployee?: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  id: string;
  createdAt: string;
  updatedAt: string;
  log?: NewApplicationLog[];
  monthlyTurnoverRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  passportNumber?: string;
  passportExpiredDate?: string;
  participatesInPolitics?: boolean;
  sourceOfFundsDescription?: string;
  similarApplications?: { edges: { node: Match }[] };
  reviewedDuplicates?: string[];
  typeOfActivityDescription?: string;
  nameOfEmployer?: string;
  employeePosition?: string;
  countryOfEmployerId?: string;
  comments?: {
    edges: {
      node: ApplicationComment;
    }[];
  };
  basePersonalInfoEditable: boolean;
  isUSResident?: boolean;
  sourcesOfFunds?: { edges: { node: { id: string } }[] };
  clientActivityId?: string;
  deliveryChannelId?: string;
  products?: { edges: { node: { id: string } }[] };
  nationalityId?: string;
  deletedAt?: string;
  relatedApplications?: NewApplicationChildrenInfo[];
  clientsScoreResult?: {
    totalRiskScore: number;
  };
  hasQuestionnaire?: boolean;
  user?: Client;
  placeOfBirth?: string;
  birthCountryId?: string;
  correspondenceAddressSameAsRegisteredAddress?: boolean;
  correspondenceCountryId?: string;
  correspondenceCity?: string;
  correspondencePostalCode?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum AgreementType {
  GENERAL_T_AND_CS = 'GENERAL_T_AND_CS',
  PRIVACY_NOTICE = 'PRIVACY_NOTICE',
  ANNEX_1 = 'ANNEX_1',
  ANNEX_2 = 'ANNEX_2',
  ANNEX_3 = 'ANNEX_3',
  ANNEX_4 = 'ANNEX_4',
  ANNEX_5 = 'ANNEX_5',
  ANNEX_6 = 'ANNEX_6',
  ANNEX_7 = 'ANNEX_7',
  ANNEX_8 = 'ANNEX_8',
}

export interface ApplicationAgreement {
  archivedAt: string;
  client: OrganizationType;
  createdAt: string;
  description: string;
  id: string;
  signatures: {
    edges: { node: { id: string; signedAt: string } }[];
  };
  startAt: string;
  type: AgreementType;
  uploadId: string;
  version: number;
  render?: Record<string, React.JSX.Element>;
}

export interface CompanyOfficialApplication {
  id: string;
  name: string;
  status: NewApplicationStatus;
  reviewStatus: ReviewStatus;
  customerId: string;
  deletedAt?: string;
  position: string;
}

export interface NewApplicationCorporateSteps {
  stepOne: NewApplicationStepStatus;
  stepTwo: NewApplicationStepStatus;
  stepThree: NewApplicationStepStatus;
  stepFour?: NewApplicationStepStatus;
  stepFive?: NewApplicationStepStatus;
  stepSix?: NewApplicationStepStatus;
  stepSeven?: NewApplicationStepStatus;
  stepEight?: NewApplicationStepStatus;
}

export enum NatureOfControlEnum {
  DIRECT_CONTROL = 'DIRECT_CONTROL',
  INDIRECT_CONTROL = 'INDIRECT_CONTROL',
}

export enum CofApprovalStatus {
  INPROCESS = 'INPROCESS',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export enum SignatureType {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
}

export interface NewApplicationChildrenInfo {
  applicationId: string;
  companyOfficialType: string;
  createdAt: string;
  id: string;
  natureOfControl: string;
  percentage: string;
  position: string;
  status?: CofApprovalStatus;
  subApplication: NewApplicationListItem;
  subApplicationId: string;
  signatureType: SignatureType;
  accessRight: AccessRight;
}

export enum ApplicationDocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export interface ApplicationDocument {
  name: string;
  uploadId: string;
  category: ApplicationDocumentType;
  documentCategoryId?: string;
  expirationDate: string;
  status: ApplicationDocumentStatus;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  isSelected?: boolean;
  comments?: { edges: { node: DocumentComment }[] };
  commentsList?: DocumentComment[];
  upload?: {
    mimeType?: string;
  };
  countComments?: {
    count: number;
  };
  warning?: boolean;
}

export interface NewApplicationCorporateInfo {
  externalUserId: string;
  userId: string;
  organizationId: string;
  sumSubApplicationId: string;
  applicationSchemeId?: string;
  applicationScheme?: ApplicationScheme;
  sumSubStatus: NewApplicationSumSubStatus;
  status: NewApplicationStatus;
  reviewStatus: ReviewStatus;
  type: ApplicationType;
  duplicates: string[];
  similarApplications?: { edges: { node: Match }[] };
  reviewEmployeeId?: string;
  hasQuestionnaire?: boolean;
  reviewEmployee?: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  comments?: {
    edges: {
      node: ApplicationComment;
    }[];
  };
  reviewedDuplicates: string[];
  reviewDuplicatesComment?: string;
  monthlyTurnoverRangeId: string;
  monthlyNumberIncomingTransactionsRangeId: string;
  monthlyNumberOutgoingTransactionsRangeId: string;
  monthlyValueIncomingTransactionsRangeId: string;
  monthlyValueOutgoingTransactionsRangeId: string;
  monthlyMaxValueIncomingPaymentRangeId: string;
  monthlyMaxValueOutgoingPaymentRangeId: string;
  registeredCountryId: string;
  registeredCity: string;
  registeredPostalCode: string;
  registeredAddress1: string;
  registeredAddress2: string;
  businessCountryId: string;
  businessAddressSameAsRegisteredAddress: boolean;
  businessCity: string;
  businessPostalCode: string;
  businessAddress1: string;
  businessAddress2: string;
  correspondenceCountryId: string;
  correspondenceAddressSameAsRegisteredAddress: boolean;
  correspondenceCity: string;
  correspondencePostalCode: string;
  correspondenceAddress1: string;
  correspondenceAddress2: string;
  vatNumber?: string;
  products?: {
    edges: { node: { id: string } }[];
  };
  log?: NewApplicationLog[];
  suppliers: {
    applicationId: string;
    monthlyTurnoverRangeId: string;
    name: string;
    id: string;
  }[];
  customers: {
    applicationId: string;
    monthlyTurnoverRangeId: string;
    name: string;
    id: string;
  }[];
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  createdByCustomer?: boolean;
  companyOfficial?: NewApplicationChildrenInfo[];
  mainRepresentative?: NewApplicationChildrenInfo[];
  countryOfIncorporationId: string;
  companyName: string;
  dateOfIncorporation: string;
  companyNumber: string;
  sicCodes?: string[];
  businessLicense: string;
  websiteLink: string;
  countriesOfInterestIds: string[];
  businessDescription: string;
  deliveryChannelId?: string;
  children?: { edges: { node: NewApplicationChildrenInfo }[] };
  relatedApplications?: NewApplicationChildrenInfo[];
  clientsScoreResult?: {
    totalRiskScore: number;
  };
  user?: Client;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  requiresBusinessLicense?: boolean | string;
  questionnaire?: {
    id: string;
  };
}

export interface CorporateFormValues {
  applicationSchemeId?: string;
  countryOfIncorporationId?: string;
  companyName?: string;
  registeredCountryId?: string;
  registeredCity?: string;
  registeredPostalCode?: string;
  registeredAddress1?: string;
  registeredAddress2?: string;
  businessAddressSameAsRegisteredAddress?: boolean;
  businessCountryId?: string;
  businessCity?: string;
  businessPostalCode?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  correspondenceAddressSameAsRegisteredAddress?: boolean;
  correspondenceCountryId?: string;
  correspondenceCity?: string;
  correspondencePostalCode?: string;
  correspondenceAddress1?: string;
  correspondenceAddress2?: string;
  dateOfIncorporation?: string;
  companyNumber?: string;
  sicCodes?: string[];
  businessLicense?: string;
  websiteLink?: string;
  countriesOfInterestIds?: string[];
  suppliers?: { name: string; monthlyTurnoverRangeId: string }[];
  customers?: { name: string; monthlyTurnoverRangeId: string }[];
  businessDescription?: string;
  monthlyTurnoverRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  reviewDuplicatesComment?: string;
  reviewedDuplicates?: string[];
  mainRepresentativeNatureOfControl?: string;
  productIds?: string[];
  mainRepresentative?: NewApplicationChildrenInfo[];
  companyOfficial?: NewApplicationChildrenInfo[];
  documents?: ApplicationDocument[];
  deliveryChannelId?: string;
  vatNumber?: string;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  requiresBusinessLicense?: boolean | string | null;
}

export interface CompanyOfficialFormValues {
  email?: string;
  dob?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  monthlyTurnoverRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  applicationId?: string;
  position?: string;
  percentage?: string;
  companyOfficialType?: ApplicationType;
  natureOfControl?: string;
  nationalityId?: string;
  deliveryChannelId?: string;
  isUSResident?: string | boolean;
  participatesInPolitics?: string | boolean;
  signatureType?: string;
  applicantId?: string;
  accessRight?: AccessRight;
  applicationSchemeId?: string;
}

export interface AssignCompanyOfficialFormValues {
  companyOfficialApplicationId: string;
  position?: string;
  percentage?: string;
  companyOfficialType: string;
  natureOfControl?: string;
}

export interface Comment {
  author: {
    firstName: string;
    lastName: string;
  };
  date: string;
  text: string;
}

export type OnSubmitType = (
  e?: React.BaseSyntheticEvent | undefined,
) => Promise<void>;

export interface SystemLogs {
  additionalInformation: string;
  applicationId: string;
  createdAt: string;
  entity: string;
  error: string;
  id: string;
  status: string;
}

export interface DocumentFormValues {
  documentPath?: string;
  documentGroup?: ApplicationDocumentType;
  documentName?: string;
}

export interface AllowedCategory {
  id: string;
  name: string;
  required: boolean;
  category?: string;
}

export interface DocumentsStats {
  count: number;
  documentCategoryId: string;
}

export interface AgreementFormTypes {
  agreement?: ApplicationAgreement;
  agreementId?: string;
}

export interface NewApplicationMainRepresentativeInfoTypes {
  isCompanyOfficial?: boolean;
}

export interface DictionaryItem {
  code?: string;
  createdAt: string;
  deletedAt?: string;
  description?: string;
  dictionaryId: string;
  id: string;
  riskScore?: number;
  updatedAt: string;
  value: string;
}

export interface AccountsCurrencies {
  currencyId: string;
  currency: Currency;
  accountOpeningPurpose: string;
  plannedTransactionNature: string;
  questionnaireId: string;
  id: string;
}

export interface AccountsCurrenciesFormValues {
  id?: string;
  currencyId: string;
  accountOpeningPurpose: string;
  plannedTransactionNature: string;
}

export interface AccountsFlows {
  transactionType: string;
  currencyId: string;
  expectedAnnualCreditTurnover: number;
  maxTransferAmountPerTransaction: number;
  transactionFrequency: number;
  transactionFrequencyPeriodMonths: number;
  questionnaireId: string;
  id: string;
}

export interface AccountsFlowsFormValues {
  id?: string;
  transactionType: string;
  currencyId: string;
  expectedAnnualCreditTurnover: string | number;
  maxTransferAmountPerTransaction: string | number;
  transactionFrequency: string | number;
  transactionFrequencyPeriodMonths: string | number;
}

export interface ExpectedFundsOrigins {
  currencyId: string;
  mainCounterpartiesAndWebsites?: string;
  countryOfIncorporationId: string;
  counterpartyBankCountryIds: string[];
  activityCountryIds: string[];
  mainActivities: string;
  transactionReason: string;
  transactionType: string;
  questionnaireId: string;
  id: string;
}

export interface ExpectedFundsOriginsFormValues {
  id?: string;
  currencyId: string;
  mainCounterpartiesAndWebsites?: string;
  countryOfIncorporationId: string;
  counterpartyBankCountryIds: string[];
  activityCountryIds: string[];
  mainActivities: string;
  transactionReason: string;
  transactionType: string;
}

export interface CRSAndFatca {
  residenceCountryId: string;
  taxIdentificationNumber?: string;
  noTinReason?: string;
  questionnaireId: string;
  id: string;
}

export interface CRSAndFatcaFormValues {
  id?: string;
  residenceCountryId: string;
  taxIdentificationNumber: string;
  noTinReason: string;
}

export interface AffilatedCompany {
  name: string;
  registrationNumber: string;
  mainActivities: string;
  position: string;
  countryId: string;
  percentageOfOwnership: string;
  questionnaireId: string;
  id: string;
}

export interface AffilatedCompanyFormValues {
  id?: string;
  name: string;
  registrationNumber: string;
  mainActivities: string;
  position: string;
  countryId: string;
  percentageOfOwnership: number | string;
}

export enum ApplicationSchemeType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}
